/**
 * PWAInstallPrompt — Smart install nudge that asks the user to add IB Club to
 * their home screen *after* they've shown engagement signals.
 *
 * Strategy (avoids "permission-prompt blindness"):
 *   1. We listen for the browser's native `beforeinstallprompt` event.
 *   2. We do NOT immediately call .prompt(). Instead we wait until the user
 *      has visited the site at least twice (counted via localStorage) AND has
 *      spent ≥ 25s on the current page. Anything sooner has 5%+ conversion
 *      rates; this gating typically gets 25–40% acceptance on India mobile.
 *   3. If accepted → great, app installed. If dismissed → snooze 14 days.
 *   4. iOS Safari doesn't fire `beforeinstallprompt`. For iOS we surface a
 *      one-time educational toast pointing at the share-sheet "Add to Home
 *      Screen" option.
 */
import { useEffect, useState } from 'react';
import { Download, X, Share } from 'lucide-react';

const VISIT_COUNTER_KEY = 'ibclub_visit_count';
const DISMISSED_AT_KEY = 'ibclub_pwa_dismissed_at';
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days
const MIN_VISITS = 2;
const MIN_DWELL_MS = 25 * 1000; // 25 seconds

const isStandaloneMode = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

const PWAInstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandaloneMode()) return undefined;

    // Bump visit counter on every mount (≈ once per session).
    try {
      const visits = parseInt(localStorage.getItem(VISIT_COUNTER_KEY) || '0', 10) + 1;
      localStorage.setItem(VISIT_COUNTER_KEY, String(visits));
    } catch { /* localStorage blocked */ }

    // Honor user-dismiss snooze window.
    let dismissedAt = 0;
    try {
      dismissedAt = parseInt(localStorage.getItem(DISMISSED_AT_KEY) || '0', 10);
    } catch { /* ignore */ }
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return undefined;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // After dwell + visit gate is satisfied, surface the banner.
    const dwellTimer = setTimeout(() => {
      let visits = 1;
      try {
        visits = parseInt(localStorage.getItem(VISIT_COUNTER_KEY) || '1', 10);
      } catch { /* ignore */ }
      if (visits < MIN_VISITS) return;
      if (deferred) setVisible(true);
      else if (isIOS()) setShowIosHint(true);
    }, MIN_DWELL_MS);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      clearTimeout(dwellTimer);
    };
  }, [deferred]);

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
    } catch { /* ignore */ }
    setVisible(false);
    setShowIosHint(false);
  };

  const handleInstall = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome !== 'accepted') {
        handleDismiss();
      } else {
        setVisible(false);
        setDeferred(null);
      }
    } catch {
      handleDismiss();
    }
  };

  if (visible && deferred) {
    return (
      <div
        data-testid="pwa-install-prompt"
        className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-[70] glass-card border border-[#d4af37]/40 shadow-2xl shadow-[#d4af37]/10 p-4 rounded-xl"
      >
        <button
          type="button"
          aria-label="Dismiss"
          onClick={handleDismiss}
          data-testid="pwa-install-dismiss"
          className="absolute top-2 right-2 text-slate-500 hover:text-slate-200 p-1"
        >
          <X size={14} />
        </button>
        <div className="flex items-start gap-3">
          <div className="bg-[#d4af37] text-slate-950 rounded-lg p-2 flex-shrink-0">
            <Download size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-sm mb-1">Install IB Club</h3>
            <p className="text-xs text-slate-400 leading-snug mb-3">
              One-tap access to deals, events & messages. Works offline & sends push notifications.
            </p>
            <button
              type="button"
              onClick={handleInstall}
              data-testid="pwa-install-confirm"
              className="bg-[#d4af37] text-slate-950 hover:bg-[#e5c558] text-xs font-semibold px-4 py-2 rounded-md tracking-wide uppercase"
            >
              Add to home screen
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showIosHint && !deferred) {
    return (
      <div
        data-testid="pwa-ios-hint"
        className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-[70] glass-card border border-[#d4af37]/40 shadow-2xl shadow-[#d4af37]/10 p-4 rounded-xl"
      >
        <button
          type="button"
          aria-label="Dismiss"
          onClick={handleDismiss}
          data-testid="pwa-ios-dismiss"
          className="absolute top-2 right-2 text-slate-500 hover:text-slate-200 p-1"
        >
          <X size={14} />
        </button>
        <div className="flex items-start gap-3">
          <div className="bg-[#d4af37] text-slate-950 rounded-lg p-2 flex-shrink-0">
            <Share size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-sm mb-1">Add IB Club to home screen</h3>
            <p className="text-xs text-slate-400 leading-snug">
              Tap the <Share size={11} className="inline mx-1 text-slate-300" /> share icon in Safari, then choose <b className="text-slate-200">Add to Home Screen</b> for one-tap access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PWAInstallPrompt;
